<template>
  <Layout>
    <div class="mc-list-header">
      <McAlert
        v-if="isFilterShortcutActive && !isCurrentShift"
        withIcon
        variant="warning"
        class="mb-3"
        :text="$t('controlTower.modal.realTimeData')"
      />
      <PageHeader :title="title" :subTitle="subTitle">
        <template #afterTitle>
          <McButton
            variant="secondary"
            icon="icon-outline_settings"
            :text="$t('notification.object.notificationSetting')"
            @click="openNotificationSetting"
          />
        </template>
      </PageHeader>
    </div>

    <section class="mc-control-tower">
      <div class="mc-control-tower__top d-flex justify-content-between mb-3">
        <div class="mc-table-filter">
          <div class="mc-table-filter__legend">
            <div
              v-if="hasAccess(['e4e-alert-center-urgent:read'])"
              class="mc-table-filter__legend-item danger"
            >
              {{ $t('controlTower.alertUrgent') }}
            </div>
            <div
              class="mc-table-filter__legend-item"
              :class="hasAccess(['e4e-alert-center-urgent:read']) ? 'important' : 'danger'"
            >
              {{ $t('controlTower.alertImportant') }}
            </div>
            <div class="mc-table-filter__legend-item success">
              {{ $t('liveView.filter.checked') }}
            </div>
            <div class="mc-table-filter__legend-item">
              {{ $t('liveView.filter.offline') }}
            </div>
          </div>
        </div>
        <div class="d-flex mc-table-filter__group">
          <ViewSetting
            v-if="refresh"
            class="me-3"
            :defaultColumnOrder="defaultColumnOrder"
            :currentColumnOrder="currentFields"
            :draggable="false"
            :max="4"
            :loading="loading"
            @setColumn="globalSettingField"
            @setDisplay="onDisplayChange"
          />
          <McButton
            class="me-2"
            variant="secondary"
            :icon="isExpanded ? 'icon-outline_collapse' : 'icon-outline_expand'"
            text=""
            :disabled="loading"
            @click="toggleExpand"
          />
          <mc-date-picker
            ref="ctDatePicker"
            v-model="period"
            class="mc-control-tower__date-picker"
            format="DD MMM YYYY - HH:mm"
            value-type="YYYY-MM-DD HH:mm:ss"
            type="datetime"
            :clearable="false"
            range-separator=" - "
            range
            :draft="true"
            :confirm="true"
            :confirm-text="$t('global.action.apply')"
            :disabled-date="disabledRange"
            @input="changeDate"
            @pick="pickDate"
            @close="closePicker"
            @open="fetchShifts"
          >
            <template #sidebar>
              <div class="ct-datepicker-sidebar">
                <button
                  v-for="(shortcut, i) in makeShortcuts"
                  :key="i"
                  class="mx-btn mx-btn-text"
                  :class="{
                    'ct-datepicker-sidebar-btn': hasAccess(['c17a-default-shift:read']),
                    active: shortcut.value
                      ? isStandardShortcutActive(shortcut)
                      : isActiveShortcut(shortcut),
                  }"
                  @click="selectShortcut(shortcut)"
                  v-html="shortcut.text"
                ></button>
              </div>
            </template>
          </mc-date-picker>
          <ModalFilter :activeFilters="filter" :loading="loading" @applyFilters="onFiltered" />
        </div>
      </div>
      <div class="d-flex justify-content-between">
        <b-row class="mc-control-tower__widget">
          <b-skeleton-wrapper :loading="loading">
            <template #loading>
              <b-row>
                <b-col md="2">
                  <div class="mc-control-tower__widget-list">
                    <b-skeleton width="60%" class="mb-2" />
                    <b-skeleton width="50%" class="mb-2" />
                    <b-skeleton width="40%" />
                  </div>
                </b-col>
                <b-col md="2">
                  <div class="mc-control-tower__widget-list">
                    <b-skeleton width="60%" class="mb-2" />
                    <b-skeleton width="50%" class="mb-2" />
                    <b-skeleton width="40%" />
                  </div>
                </b-col>
                <b-col md="2">
                  <div class="mc-control-tower__widget-list">
                    <b-skeleton width="60%" class="mb-2" />
                    <b-skeleton width="50%" class="mb-2" />
                    <b-skeleton width="40%" />
                  </div>
                </b-col>
              </b-row>
            </template>
            <b-col md="3">
              <div class="mc-control-tower__widget-list align-items-end">
                <div class="d-flex gap-2 align-items-center">
                  <div class="avatar-xs">
                    <div class="avatar-title rounded-circle bg-danger bg-soft">
                      <i class="icon-danger icon-outline_truck-maintenance"></i>
                    </div>
                  </div>
                  <span>
                    <strong>{{ unresolvedVehicles }}</strong>
                    {{ $t('controlTower.vehicleUnchecked') }}
                  </span>
                </div>
                <McToggle
                  v-model="showUnresolved"
                  :label="$t('global.view')"
                  placement="left"
                  @change="handleShowUnresolved"
                />
              </div>
            </b-col>
            <b-col md="3">
              <div class="mc-control-tower__widget-list">
                <div class="d-flex gap-2 align-items-center">
                  <div class="avatar-xs">
                    <div class="avatar-title rounded-circle bg-danger bg-soft">
                      <i class="icon-danger icon-outline_alert-triangle"></i>
                    </div>
                  </div>
                  <span>
                    <strong>{{ totalUnresolvedAlerts }}</strong>
                    {{ $t('controlTower.alertUnchecked') }}
                  </span>
                </div>
                <McButton
                  variant="secondary"
                  text="Detail"
                  size="sm"
                  @click="showUnresolvedAlerts()"
                />
              </div>
            </b-col>
            <b-col md="3">
              <div class="mc-control-tower__widget-list">
                <div class="d-flex gap-2 align-items-center">
                  <div class="avatar-xs">
                    <div class="avatar-title rounded-circle bg-success bg-soft">
                      <i class="icon-success icon-outline_submitted"></i>
                    </div>
                  </div>
                  <span>
                    <strong>{{ totalResolvedAlerts }}</strong>
                    {{ $t('controlTower.alertChecked') }}
                  </span>
                </div>
                <McButton
                  variant="secondary"
                  text="Detail"
                  size="sm"
                  @click="showResolvedAlerts()"
                />
              </div>
            </b-col>
          </b-skeleton-wrapper>
        </b-row>
        <b-skeleton-wrapper
          v-if="isFilterShortcutActive && previousShiftUnresolvedAlerts > 0"
          :loading="loading"
        >
          <template #loading>
            <div class="mc-control-tower__widget-shift">
              <div class="d-flex align-items-center justify-content-between w-100">
                <div class="d-flex align-items-center">
                  <b-skeleton type="avatar" size="sm" class="me-2" />
                  <b-skeleton width="200px" />
                </div>
                <b-skeleton width="40px" />
              </div>
            </div>
          </template>
          <div class="mc-control-tower__widget-shift">
            <p>
              <i class="mc-fa icon-fill_info"></i> {{ $t('dashboard.scheduler.alert.check') }}
              <strong>{{ previousShiftUnresolvedAlerts }}</strong>
              {{ $t('controlTower.modal.alertFromPreviousShift') }}
            </p>
            <span @click="showPreviousShiftUnresolvedAlerts()">{{ $t('global.view') }}</span>
          </div>
        </b-skeleton-wrapper>
      </div>
      <div
        v-if="displayView !== 'fleetTask' && groupedByStatus.favorite?.length > 0"
        class="mc-control-tower--favorit"
      >
        <CTSection
          :title="$t('global.object.favorite')"
          :data="groupedByStatus.favorite"
          :isLoading="loading"
          :isExpanded="isExpanded"
          :listKeys="listKeys"
          @showDetail="showDetail"
          @setPin="setPin"
        />
      </div>

      <div v-if="displayView === 'motionstatus'" class="mc-control-tower--status">
        <CTSection
          v-for="(section, key) in motionStatusSection"
          :key="key"
          :title="section.title"
          :data="groupedByStatus[key]"
          :isLoading="loading"
          :isExpanded="isExpanded"
          :listKeys="listKeys"
          @showDetail="showDetail"
          @setPin="setPin"
        />
      </div>

      <div v-if="displayView === 'fleetgroup'" class="mc-control-tower--group">
        <CTSection
          v-for="(group, groupName) in groupedByFleet"
          :key="groupName"
          :title="groupName"
          :data="group"
          :isLoading="loading"
          :isExpanded="isExpanded"
          :listKeys="listKeys"
          @showDetail="showDetail"
          @setPin="setPin"
        />
      </div>

      <div v-if="displayView === 'all'" class="mc-control-tower--all">
        <CTSection
          :title="$t('liveView.filter.allFleet')"
          :data="groupedByStatus.all"
          :isLoading="loading"
          :isExpanded="isExpanded"
          :listKeys="listKeys"
          @showDetail="showDetail"
          @setPin="setPin"
        />
      </div>

      <div v-if="displayView === 'fleetTask'">
        <CTSection
          v-for="group in fleetTaskGroups"
          :key="group.id"
          :title="group.title"
          :data="groupedByFleetTask[group.id]"
          :isLoading="loading"
          :isExpanded="isExpanded"
          :listKeys="listKeys"
          @showDetail="showDetail"
          @setPin="setPin"
        />
      </div>

      <ModalDetail
        v-for="popup in popupLists"
        :key="popup.id"
        :item="popup.data"
        :callState="callState[popup.id]"
        :position="{
          x: popup.x,
          y: popup.y,
        }"
        :period="period"
        :urgentAlertRules="urgentAlertRules"
        :importantAlertRules="importantAlertRules"
      />
      <ModalUnresolved :period="activeUnresolvedPeriod" @resolved="handleResolvedAlerts" />
      <ModalResolved :period="period" />
      <ModalAddShift />
    </section>
  </Layout>
</template>

<script>
import { computed } from 'vue'
import appConfig from '@/app.config'
import { mapActions, mapState, mapGetters } from 'vuex'
import moment from 'moment'

import PageHeader from '@/components/page-header-v2'
import Layout from '@/router/layouts/main'
import ViewSetting from './Modal/ViewSetting'
import ModalFilter from './Modal/ModalFilter'
import ModalDetail from './Modal/ModalDetail'
import ModalUnresolved from './Modal/ModalUnresolved'
import ModalResolved from './Modal/ModalResolved'
import ModalAddShift from './Modal/ModalAddShift'
import CTSection from './component/CTSection'
import McToggle from '@/components/McToggle'
import McButton from '@/components/McButton'
import McAlert from '@/components/McAlert'
import McDatePicker from '@/components/McDatePicker'

import { GET_VEHICLE_STATUSES_CONTROL_TOWER_V2 } from '@/services/api/fleet'
import { CTOWER_USER_PLACARDS, UPDATE_CTOWER_USER_PLACARD } from '@/services/api/controltower'
import * as FleetTaskInstant from '@/services/rest-api/lib/fleet-task-instant/fleet-task-instant'
import msgpack from 'msgpack-lite'
import responsive from '@/mixin/responsive'
import { Helper } from '@/mixin/helper'
import { MixPanel } from '@/mixin/mixpanel'
const soundNotification = require('@/assets/sounds/notification_sound.wav')
import { GetAddressData } from '@/services/api/map'
import { EventBus } from '@/router/views/fleet-task/lib/event-bus'
import { GET_VEHICLE_TV_DATA } from '@/services/api/trackvideo/video'
import { GET_SHIFTS } from '@/services/api/setting/shift/shift'
import { TOTAL_IMPORTANT_ALERTS } from '@/services/api/driverscore/score'

/**
 * Starter component
 */
export default {
  page: {
    title: 'Control Tower',
    meta: [{ name: 'description', content: appConfig.description }],
  },

  components: {
    Layout,
    PageHeader,
    ModalFilter,
    ModalDetail,
    ViewSetting,
    McToggle,
    CTSection,
    McButton,
    ModalUnresolved,
    ModalResolved,
    ModalAddShift,
    McDatePicker,
    McAlert,
  },
  mixins: [responsive, Helper, MixPanel],
  apollo: {},
  provide() {
    return {
      isShowFleetTask: computed(() => this.isShowFleetTask),
    }
  },
  data() {
    return {
      refresh: true,
      trigger: 0,
      calculatedData: [],
      vehicles: null,
      vehicleStatusesObj: null,
      detail: {},
      timeout: {},
      oldDataColumn: [],
      currentFields: [],
      loading: true,
      valDef: ['speed', 'status', 'travelTime', 'mileage'],
      lastPong: moment(),
      pongInterval: null,
      forceDisconnect: null,
      filter: {
        vehicleId: [],
        motionStatus: ['M', 'I', 'A', 'P', 'O'],
        assignmentType: 'all',
      },
      isExpanded: true,
      collapsedSections: {
        favorite: false,
        moving: false,
        idle: false,
        assisted: false,
        parking: false,
        offline: false,
        all: false,
      },
      displayView: 'motionstatus',
      showImportant: true,
      showUnresolved: false,
      pageViewTImer: null,

      intervalFleetTask: null,
      timeIntervalFO: 15000,
      fleetTaskMap: new Map(),
      isShowFleetTask: false,
      shifts: [],
      period: [
        moment().subtract(1, 'days').format('YYYY-MM-DD 00:00:00'),
        moment().format('YYYY-MM-DD 23:59:00'),
      ],
      pastDate: moment().subtract(6, 'months'),
      futureEnd: moment().add(6, 'months'),
      currentPastDate: moment().subtract(6, 'months'),
      currentFutureEnd: moment().add(6, 'months'),
      tempUnresolvedAlerts: 0,
      addressCache: {},
      loadingAddresses: new Set(),
      lastGpsLatLong: {},
      gpsLatLongInterval: null,
      urgentAlertRules: [],
      importantAlertRules: [],
      timeUpdateInterval: null,
      currentTime: moment(),
      childToParent: {},

      // key is vehicleId parent
      // value is interface with
      // * isHaveVoip,
      // * deviceType,
      // * imeiVoip,
      // * vehicleIdVoip,
      callState: {},
      wsMessageHandler: null,
      shiftShortcutLimit: 7,
      isFilterShortcutActive: false,
      previousShiftUnresolvedAlerts: 0,
      previousShiftPeriod: [],
      activeUnresolvedPeriod: [],
      isCurrentShift: false,
    }
  },

  computed: {
    ...mapState('controlTower', ['activePopups']),
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
      hasAccessAll: 'identity/hasAccessAll',
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),

    title() {
      return this.$t('menuitems.controlTower.text')
    },

    subTitle() {
      return this.$t('liveView.text.controlTowerDesc')
    },

    defaultColumnOrder() {
      const columnOrder = [
        {
          key: 'fuelPercentage',
          label: `${this.$t('liveView.infoWindow.fuel')} (%)`,
          shown: false,
        },
        {
          key: 'fuelTotal',
          label: `${this.$t('liveView.infoWindow.fuel')} (L)`,
          shown: false,
        },
        {
          key: 'mileage',
          label: `${this.$t('liveView.infoWindow.distanceTraveled')}`,
          shown: true,
        },
        {
          key: 'speed',
          label: `${this.$t('liveView.infoWindow.speed')}`,
          shown: true,
        },
        {
          key: 'engine',
          label: this.$t('global.alert.machine'),
          shown: false,
        },
        {
          key: 'refrigerator',
          label: this.$t('master.list.tracker.refrigerator'),
          shown: false,
        },
        {
          key: 'door1',
          label: this.$t('global.alert.door1'),
          shown: false,
        },
        {
          key: 'door2',
          label: this.$t('global.alert.door2'),
          shown: false,
        },
        {
          key: 'pto',
          label: 'PTO',
          shown: false,
        },
        {
          key: 'status',
          label: this.$t('global.object.fleetStatus'),
          shown: true,
        },
        {
          key: 'temp1',
          label: this.$t('liveView.infoWindow.temp1'),
          shown: false,
        },
        {
          key: 'temp2',
          label: this.$t('liveView.infoWindow.temp2'),
          shown: false,
        },
        {
          key: 'temp3',
          label: this.$t('liveView.infoWindow.temp3'),
          shown: false,
        },
        {
          key: 'travelTime',
          label: this.$t('liveView.infoWindow.elapsedTime'),
          shown: true,
        },
        {
          key: 'lastUpdate',
          label: this.$t('videoHistory.video.lastUpdate'),
          shown: false,
        },
      ]

      if (this.hasLastLocationScope) {
        columnOrder.push({
          key: 'location',
          label: this.$t('global.object.location'),
          shown: false,
        })
      }

      if (this.hasFleetTaskInstantScope) {
        columnOrder.unshift({
          key: 'fleetTaskNumber',
          label: this.$t('controlTower.detail.fleetTaskNumber'),
          shown: true,
        })
      }

      return columnOrder
    },

    groupedByStatus() {
      const groups = {
        favorite: this.sortByAlertPriority(this.calculatedData.filter((item) => item.isPinned)),
        moving: this.sortByAlertPriority(
          this.calculatedData.filter((item) => {
            if (item.children?.[0]) {
              const isChildFresh = this.isDataFresh(item.children[0].lastPositionStatus)
              if (isChildFresh) return true
            }
            return item.calculatedMotionStatus === 'M'
          })
        ),
        idle: this.sortByAlertPriority(
          this.calculatedData.filter((item) => item.calculatedMotionStatus === 'I')
        ),
        assisted: this.sortByAlertPriority(
          this.calculatedData.filter((item) => item.calculatedMotionStatus === 'A')
        ),
        parking: this.sortByAlertPriority(
          this.calculatedData.filter((item) => item.calculatedMotionStatus === 'P')
        ),
        offline: this.sortByAlertPriority(
          this.calculatedData.filter((item) => {
            if (item.children?.[0]) {
              const isChildFresh = this.isDataFresh(item.children[0].lastPositionStatus)
              if (isChildFresh) return false
            }
            return item.calculatedMotionStatus === 'U' || item.calculatedMotionStatus === 'O'
          })
        ),
        all: this.sortByAlertPriority(
          this.calculatedData.filter((item) => item.calculatedMotionStatus !== '')
        ),
      }
      return groups
    },

    groupedByFleet() {
      if (!this.calculatedData) return {}

      const groups = {}

      this.calculatedData.forEach((item) => {
        if (item.vehicleGroups && item.vehicleGroups.length) {
          const uniqueGroups = [...new Set(item.vehicleGroups)]
          uniqueGroups.forEach((group) => {
            if (!groups[group]) {
              groups[group] = []
            }
            groups[group].push(item)
          })
        }
      })

      Object.keys(groups).forEach((groupName) => {
        groups[groupName] = this.sortByAlertPriority(groups[groupName])
      })

      return groups
    },

    groupedByFleetTask() {
      const assignedWithFO = []
      const unassignedWithFO = []

      this.calculatedData.forEach((item) => {
        if (item.controlTowerData?.fleetTaskInstant?.id) {
          assignedWithFO.push(item)
        } else {
          unassignedWithFO.push(item)
        }
      })

      return {
        assignedWithFO: this.sortByAlertPriority(assignedWithFO),
        unassignedWithFO: this.sortByAlertPriority(unassignedWithFO),
      }
    },

    motionStatusSection() {
      const section = {
        moving: {
          title: this.$t('liveView.filter.moving'),
          key: 'moving',
          scope: null,
        },
        idle: {
          title: this.$t('liveView.filter.idle'),
          key: 'idle',
          scope: null,
        },
        assisted: {
          title: this.$t('liveView.filter.assisted'),
          key: 'assisted',
          scope: this.hasAccess(['assisted_motion_status:read']),
        },
        parking: {
          title: this.$t('liveView.filter.parking'),
          key: 'parking',
          scope: null,
        },
        offline: {
          title: this.$t('liveView.filter.offline'),
          key: 'offline',
          scope: null,
        },
      }

      const filteredSections = {}
      Object.keys(section).forEach((key) => {
        if (section[key].scope === null || section[key].scope === true) {
          filteredSections[key] = section[key]
        }
      })

      return filteredSections
    },

    vehicleStatuses() {
      if (this.vehicleStatusesObj === null) return []
      let items = Object.values(this.vehicleStatusesObj)
      items = items.map((item) => ({
        ...item,
        timePassed: this.getLiveTimePassed(item.lastReceive, this.currentTime),
      }))

      const seenVehicleIds = new Set()
      items = items.filter((item) => {
        if (seenVehicleIds.has(item.vehicleId)) {
          return false
        }
        seenVehicleIds.add(item.vehicleId)
        return true
      })

      items.sort(function (a, b) {
        const pinnedA = a.isPinned
        const pinnedB = b.isPinned
        const hasUnresolvedAlertsA = a.controlTowerData.unresolvedAlerts?.length > 0
        const hasUnresolvedAlertsB = b.controlTowerData.unresolvedAlerts?.length > 0
        const isOfflineA = a.calculatedMotionStatus === 'O' || a.calculatedMotionStatus === 'U'
        const isOfflineB = b.calculatedMotionStatus === 'O' || b.calculatedMotionStatus === 'U'
        const licensePlateA = a.licensePlate
        const licensePlateB = b.licensePlate

        if (isOfflineA !== isOfflineB) return isOfflineA ? 1 : -1
        if (pinnedA !== pinnedB) return pinnedA ? -1 : 1
        if (hasUnresolvedAlertsA !== hasUnresolvedAlertsB) return hasUnresolvedAlertsA ? -1 : 1
        return licensePlateA.localeCompare(licensePlateB)
      })

      if (this.filter.vehicleId.length) {
        items = items.filter((x) => this.filter.vehicleId.includes(x.vehicleId))
      }

      if (this.filter.motionStatus.length) {
        items = items.filter((x) => this.filter.motionStatus.includes(x.calculatedMotionStatus))
      }

      if (this.filter.assignmentType !== 'all') {
        items = items.filter(
          (x) =>
            (this.filter.assignmentType === 'assigned' &&
              x.controlTowerData?.fleetTaskInstant?.id) ||
            (this.filter.assignmentType === 'unassigned' &&
              !x.controlTowerData?.fleetTaskInstant?.id)
        )
      }

      if (this.showUnresolved) {
        items = items.filter(
          (x) =>
            x.controlTowerData?.important?.unresolvedAlerts?.length > 0 ||
            x.controlTowerData?.urgent?.unresolvedAlerts?.length > 0
        )
      }

      return items
    },

    listKeys() {
      return {
        fuelPercentage: {
          key: 'fuelFiltered',
          label: `${this.$t('liveView.infoWindow.fuel')} (%)`,
          addition: '%',
        },
        fuelTotal: {
          key: 'fuelCapacity',
          label: `${this.$t('liveView.infoWindow.fuel')} (L)`,
          addition: this.$t('dashboard.object.liter'),
          formatter: (row) => ((row.fuelCapacity * row.fuelFiltered) / 100).toFixed(2),
        },
        mileage: {
          key: 'sumDistance',
          label: `${this.$t('liveView.infoWindow.distanceTraveled')}`,
          addition: 'Km',
        },
        speed: {
          key: 'calculatedSpeed',
          label: `${this.$t('liveView.infoWindow.speed')}`,
          addition: `Km/${this.$t('global.time.hours')}`,
        },
        engine: {
          key: 'engineOn',
          label: this.$t('global.alert.machine'),
          addition: '',
          formatter: (row) => (row.engineOn ? 'ON' : 'OFF'),
        },
        refrigerator: {
          key: 'refrigeratorStatus',
          label: this.$t('master.list.tracker.refrigerator'),
          addition: '',
          formatter: (row) => (row.refrigeratorStatus ? 'ON' : 'OFF'),
        },
        door1: {
          key: 'door1Status',
          label: this.$t('global.alert.door1'),
          addition: '',
          formatter: (row) =>
            row.door1Status ? this.$t('global.closed') : this.$t('global.opened'),
        },
        door2: {
          key: 'door2Status',
          label: this.$t('global.alert.door2'),
          addition: '',
          formatter: (row) =>
            row.door2Status ? this.$t('global.closed') : this.$t('global.opened'),
        },
        pto: {
          key: 'ptoStatus',
          label: 'PTO',
          addition: '',
          formatter: (row) => {
            if (row.ptoStatus === null) {
              return '-'
            }

            return row.ptoStatus ? 'ON' : 'OFF'
          },
        },
        status: {
          key: 'calculatedMotionStatus',
          label: this.$t('global.object.fleetStatus'),
          addition: '',
          formatter: (row) => this.motionStatusFormat(row.calculatedMotionStatus),
        },
        temp1: {
          key: 'temperature',
          label: this.$t('liveView.infoWindow.temp1'),
          addition: '°C',
          formatter: (row) => row.temperature[0] ?? '-',
        },
        temp2: {
          key: 'temperature',
          label: this.$t('liveView.infoWindow.temp2'),
          addition: '°C',
          formatter: (row) => row.temperature[1] ?? '-',
        },
        temp3: {
          key: 'temperature',
          label: this.$t('liveView.infoWindow.temp3'),
          addition: '°C',
          formatter: (row) => row.temperature[2] ?? '-',
        },
        travelTime: {
          key: 'sumDrivetimeFormatted',
          label: this.$t('liveView.infoWindow.elapsedTime'),
          addition: '',
        },
        lastUpdate: {
          key: 'lastReceive',
          label: this.$t('videoHistory.video.lastUpdate'),
          addition: '',
          formatter: (row) => {
            return row.timePassed
          },
        },
        location: {
          key: 'latitude',
          label: this.$t('global.object.location'),
          addition: '',
          formatter: (row) => {
            return this.getLocationDisplay(row)
          },
        },
      }
    },

    vehicleProblems() {
      if (!this.vehicleStatuses.length) {
        return null
      }

      return this.vehicleStatuses.filter((x) => x.controlTowerData.status == 'U').length.toString()
    },

    vehicleCompletedInspected() {
      if (!this.vehicleStatuses.length) {
        return null
      }

      return this.vehicleStatuses.filter((x) => x.controlTowerData.status == 'R').length.toString()
    },

    vehicleCompletedInspectedData() {
      if (!this.vehicleStatuses.length) {
        return null
      }

      return this.vehicleStatuses
        .filter((x) => x.controlTowerData.status == 'R')
        .map((x) => x.controlTowerData)
    },

    fleetOffline() {
      if (!this.vehicleStatuses.length) {
        return null
      }

      return this.vehicleStatuses
        .filter((x) => x.controlTowerData.status == 'O' || x.controlTowerData.status == 'X')
        .length.toString()
    },

    unresolvedVehicles() {
      if (this.hasAccess(['e4e-alert-center-urgent:read'])) {
        return this.calculatedData.filter(
          (item) =>
            item.controlTowerData?.important?.unresolvedAlerts?.length > 0 ||
            item.controlTowerData?.urgent?.unresolvedAlerts?.length > 0
        ).length
      }
      return this.calculatedData.filter(
        (item) => item.controlTowerData?.important?.unresolvedAlerts?.length > 0
      ).length
    },

    totalUnresolvedAlerts() {
      if (!this.calculatedData?.length) {
        return 0
      }

      let total = 0

      if (this.hasAccess(['e4e-alert-center-urgent:read'])) {
        total = this.calculatedData.reduce((sum, item) => {
          const urgent = item.controlTowerData?.urgent?.totalUnresolvedAlerts || 0
          const important = item.controlTowerData?.important?.totalUnresolvedAlerts || 0
          return sum + urgent + important
        }, 0)
        total = Math.max(0, total)
        return total
      }

      total = this.calculatedData.reduce((sum, item) => {
        const important = item.controlTowerData?.important?.totalUnresolvedAlerts || 0
        return sum + important
      }, 0)
      total = Math.max(0, total)
      return total
    },

    totalResolvedAlerts() {
      if (!this.calculatedData?.length) {
        return 0
      }

      if (this.hasAccess(['e4e-alert-center-urgent:read'])) {
        return this.calculatedData.reduce((total, item) => {
          return (
            total +
            (item.controlTowerData?.urgent?.totalResolvedAlerts || 0) +
            (item.controlTowerData?.important?.totalResolvedAlerts || 0)
          )
        }, 0)
      }
      return this.calculatedData.reduce((total, item) => {
        return total + (item.controlTowerData?.important?.totalResolvedAlerts || 0)
      }, 0)
    },

    popupLists() {
      return this.activePopups
    },

    hasFleetTaskInstantScope() {
      return this.hasAccessAll([
        'fleet-task-instant:read',
        'fleet-task-instant:create',
        'fleet-task-instant:update',
        'fleet-task-instant:delete',
      ])
    },

    hasLastLocationScope() {
      return this.hasAccessAll(['h1c-control-tower-last-location:read'])
    },

    fleetTaskGroups() {
      return [
        {
          id: 'assignedWithFO',
          title: this.$t('controlTower.filter.assignedWithFO'),
        },
        {
          id: 'unassignedWithFO',
          title: this.$t('controlTower.filter.unassignedWithFO'),
        },
      ]
    },

    makeShortcuts() {
      const createStandardShortcut = (text, value) => ({ text, value, onClick: value })
      const shortcuts = [
        createStandardShortcut(this.$t('global.time.today'), () => {
            const dateStart = new Date()
            dateStart.setHours(0, 0, 0, 0)
            return [dateStart, new Date()]
          }),
        createStandardShortcut(this.$t('global.time.yesterday'), () => {
            const dateStart = new Date()
            const dateEnd = new Date()
            dateStart.setTime(dateStart.getTime() - 3600 * 1000 * 24)
            dateStart.setHours(0, 0, 0, 0)
            dateEnd.setTime(dateEnd.getTime() - 3600 * 1000 * 24)
            dateEnd.setHours(23, 59, 59, 999)
            return [dateStart, dateEnd]
          }),
        createStandardShortcut(this.$t('global.time.last3Days'), () => {
            const date = new Date()
            date.setHours(0, 0, 0, 0)
            date.setTime(date.getTime() - 3600 * 1000 * 48)
            return [date, new Date()]
          }),
        createStandardShortcut(this.$t('global.time.last7Days'), () => {
            const date = new Date()
            date.setHours(0, 0, 0, 0)
            date.setTime(date.getTime() - 3600 * 1000 * 144)
            return [date, new Date()]
          }),
      ]

      if (this.hasAccess(['c17a-default-shift:read'])) {
        if (this.shifts && this.shifts.length) {
          const shiftShortcuts = []
          const now = moment()

          for (let i = 0; i >= -30; i--) {
            if (shiftShortcuts.length >= 50) break

            const targetDay = moment().add(i, 'days')
            const isoWeekday = targetDay.isoWeekday()
            const targetDateStr = targetDay.format('YYYY-MM-DD')

            const dailyShifts = []

            this.shifts.forEach((shift) => {
              if (!shift.shiftTime || !Array.isArray(shift.shiftTime)) return

              shift.shiftTime.forEach((timeItem) => {
                const shiftDays = Array.isArray(timeItem.days) ? timeItem.days : []
                const shiftDates = Array.isArray(timeItem.dates) ? timeItem.dates : []

                if (shiftDays.some((d) => d == isoWeekday) || shiftDates.includes(targetDateStr)) {
                  const startTimeStr = timeItem.startTime
                  const endTimeStr = timeItem.endTime

                  const startTime = moment(targetDay.format('YYYY-MM-DD') + ' ' + startTimeStr)
                  const endTime = moment(targetDay.format('YYYY-MM-DD') + ' ' + endTimeStr)

                  if (endTime.isBefore(startTime)) {
                    endTime.add(1, 'days')
                  }

                  if (startTime.isSameOrBefore(now)) {
                    const isCurrent = now.isBetween(startTime, endTime, null, '[]')
                    const currentLabel = isCurrent
                      ? ' (' + this.$t('dashboard.scheduler.previewDaily.atTheMoment') + ')'
                      : ''
                    dailyShifts.push({
                      text: `${shift.name}${currentLabel}<br />${startTime.format(
                        'dddd, DD MMM'
                      )} (${startTime.format('HH:mm')} - ${endTime.format('HH:mm')})`,
                      start: startTime,
                      end: endTime,
                      onClick: () => {
                        return [startTime.toDate(), endTime.toDate()]
                      },
                      isShift: true,
                    })
                  }
                }
              })
            })

            dailyShifts.sort((a, b) => b.start.valueOf() - a.start.valueOf())
            shiftShortcuts.push(...dailyShifts)
          }

          shiftShortcuts.sort((a, b) => b.start.valueOf() - a.start.valueOf())

          shiftShortcuts.forEach((shortcut, index) => {
            if (index < shiftShortcuts.length - 1) {
              shortcut.previousShift = shiftShortcuts[index + 1]
            }
          })

          const limitedShifts = shiftShortcuts.slice(0, 50)

          const visibleShifts = limitedShifts.slice(0, this.shiftShortcutLimit)
          shortcuts.push(...visibleShifts)

          if (limitedShifts.length > this.shiftShortcutLimit) {
            shortcuts.push({
              text: this.$t('global.action.seeMore'),
              onClick: () => {
                this.shiftShortcutLimit += 7
              },
            })
          }
        } else {
          if (this.getAccessRight() && this.hasAccess(['c17a-default-shift:create'])) {
            shortcuts.push({
              text: this.$t('controlTower.modal.addShiftSchedule'),
              onClick: () => {
                this.openShiftScheduleModal()
                if (this.$refs.ctDatePicker) {
                  this.$refs.ctDatePicker.closePopup()
                }
              },
            })
          }
        }
      }

      return shortcuts
    },

    hasLocationField() {
      return (
        this.currentFields.find((field) => field.key === 'location' && field.shown === true) !==
        undefined
      )
    },
  },

  watch: {
    trigger() {
      this.calculatedData = [...this.vehicleStatuses]
    },

    '$i18n.locale': function () {
      this.currentFields = []
      this.defaultColumnOrder.forEach((d) => {
        this.currentFields.push({
          ...d,
          shown:
            this.oldDataColumn !== undefined
              ? this.oldDataColumn.includes(d.key)
              : this.valDef.includes(d.key),
        })
      })
      this.refresh = false

      setTimeout(() => {
        this.refresh = true
      }, 500)
    },

    vehicleStatusesObj: {
      deep: true,
      handler(newValue) {
        if (this.activePopups.length > 0) {
          this.activePopups.forEach((popup) => {
            if (newValue[popup.data.vehicleId]) {
              this.$store.dispatch('controlTower/updatePopupData', {
                id: popup.id,
                data: newValue[popup.data.vehicleId],
              })
            }
          })
        }
      },
    },
    '$store.state.notification.alertRuleIds': {
      handler(newVal) {
        if (newVal) {
          this.urgentAlertRules = newVal.urgentAlertRules ?? []
          this.importantAlertRules = newVal.importantAlertRules ?? []
        }
      },
      immediate: true,
      deep: true,
    },
  },

  async created() {
    await this.$nextTick()
    this.getData()
  },

  async mounted() {
    const alertRuleIds = this.$store.state.notification.alertRuleIds ?? {}

    this.urgentAlertRules = alertRuleIds.urgentAlertRules
    this.importantAlertRules = alertRuleIds.importantAlertRules

    if (!this.mediaQuery.matches) {
      this.$store.dispatch('layout/toggleMenu', true)
    }

    // Register message handler.
    this.wsMessageHandler = this.handleWebSocketMessage.bind(this)
    await this.$store.dispatch('ws/registerMessageHandler', this.wsMessageHandler)

    // Ensure websocket is connected and send payload.
    await this.ensureWebSocketConnected()

    const savedPopups = localStorage.getItem('ctPopups')
    const parsed = JSON.parse(savedPopups)

    if (parsed && parsed?.length !== 0) {
      this.$store.commit('controlTower/LOAD_POPUPS', parsed)
      parsed.forEach((popup) => {
        this.callState[popup.id] = popup.callState
      })
    }

    this.track('Open Control Tower V2')

    this.pageViewTimer = setTimeout(() => {
      this.track('Open Control Tower V2 > 5 Hours')
    }, 5 * 60 * 60 * 1000)

    this.gpsLatLongInterval = setInterval(this.applyBufferedLatLongUpdates, 5 * 60 * 1000)

    this.startExpiryChecker()
  },

  beforeDestroy() {
    this.forceDisconnect = true

    if (this.wsMessageHandler) {
      this.$store.dispatch('ws/unregisterMessageHandler', this.wsMessageHandler)
    }

    clearTimeout(this.pageViewTimer)

    if (this.hasFleetTaskInstantScope) {
      this.clearFleetTaskTimeInterval()
      window.removeEventListener('focus', this.fetchFleetTaskOnTabFocus)
      window.removeEventListener('blur', this.clearFleetTaskTimeInterval)
    }

    clearInterval(this.gpsLatLongInterval)
    clearInterval(this.timeUpdateInterval)

    this.stopExpiryChecker()
  },

  async destroyed() {
    clearInterval(this.pongInterval)
  },

  methods: {
    async fetchShifts() {
      if (!this.hasAccess(['c17a-default-shift:read'])) return
      this.shiftShortcutLimit = 7
      if (this.shifts.length > 0) return
      try {
        const { data } = await this.$apollo.query({
          query: GET_SHIFTS,
          fetchPolicy: 'network-only',
        })
        this.shifts = data.shifts?.shifts?.data || []
      } catch (error) {
        console.error('Error fetching shifts:', error)
      }
    },

    isActiveShortcut(shortcut) {
      if (!shortcut.start || !shortcut.end || !this.period || this.period.length !== 2) return false
      return (
        this.period[0] === shortcut.start.format('YYYY-MM-DD HH:mm:ss') &&
        this.period[1] === shortcut.end.format('YYYY-MM-DD HH:mm:ss')
      )
    },

    isStandardShortcutActive(shortcut) {
      if (!shortcut.value || !this.period || this.period.length !== 2) return false

      const value = shortcut.value()
      return value.every((date, index) => moment(date).isSame(moment(this.period[index]), 'day'))
    },

    selectShortcut(shortcut) {
      const result = shortcut.onClick()
      if (result) {
        const formattedResult = result.map((date) => moment(date).format('YYYY-MM-DD HH:mm:ss'))
        this.period = formattedResult
        this.isFilterShortcutActive = !!shortcut.isShift

        if (shortcut.isShift && shortcut.start && shortcut.end) {
          this.isCurrentShift = moment().isBetween(shortcut.start, shortcut.end, null, '[]')
        } else {
          this.isCurrentShift = !!shortcut.isCurrent
        }

        this.changeDate()
        if (this.$refs.ctDatePicker) {
          this.$refs.ctDatePicker.closePopup()
        }

        if (shortcut.isShift && shortcut.previousShift) {
          const prevResult = shortcut.previousShift.onClick()
          this.previousShiftPeriod = prevResult.map((date) =>
            moment(date).format('YYYY-MM-DD HH:mm:ss')
          )
          this.fetchPreviousShiftStats(prevResult)
        } else {
          this.previousShiftUnresolvedAlerts = 0
          this.previousShiftPeriod = []
        }
      }
    },

    async fetchPreviousShiftStats(period) {
      try {
        const { data: res } = await this.$apollo.query({
          query: TOTAL_IMPORTANT_ALERTS,
          fetchPolicy: 'no-cache',
          variables: {
            filter: {
              startDate: moment(period[0]).set('second', 0).toISOString(),
              endDate: moment(period[1]).set('second', 59).toISOString(),
            },
          },
        })

        if (res.totalImportantAlerts.isSuccessful) {
          const alerts = res.totalImportantAlerts.totalImportantAlerts || []
          let total = 0

          const urgentAlerts = alerts.find((a) => a.alertUrgencyType === 'Urgent')
          const importantAlerts = alerts.find((a) => a.alertUrgencyType === 'Important')

          const urgentUnresolved = urgentAlerts ? urgentAlerts.unresolved : 0
          const importantUnresolved = importantAlerts ? importantAlerts.unresolved : 0

          if (this.hasAccess(['e4e-alert-center-urgent:read'])) {
            total = urgentUnresolved + importantUnresolved
          } else {
            total = importantUnresolved
          }

          this.previousShiftUnresolvedAlerts = Math.max(0, total)
        }
      } catch (error) {
        console.error('Error fetching previous shift stats:', error)
        this.previousShiftUnresolvedAlerts = 0
      }
    },

    ...mapActions('announcement', [
      'incrementUnreadAnnouncement',
      'decrementUnreadAnnouncement',
      'resetUnreadAnnouncement',
      'setInitialUnreadAnnouncement',
    ]),

    async getData() {
      this.loading = true

      if (this.trigger === 0) {
        try {
          const viewSettingStateStr = localStorage.getItem('viewSettingState')

          if (viewSettingStateStr) {
            const viewSettingState = JSON.parse(viewSettingStateStr)

            if (viewSettingState.displayType) {
              const validTypes = ['motionstatus', 'fleetgroup', 'all', 'fleetTask']

              if (validTypes.includes(viewSettingState.displayType)) {
                this.displayView = viewSettingState.displayType
              }
            }
          }
        } catch (error) {
          return
        }
      }

      const { data: resVehicleStatuses } = await this.$apollo.query({
        client: 'clientLowPrio',
        query: GET_VEHICLE_STATUSES_CONTROL_TOWER_V2,
        fetchPolicy: 'no-cache',
        variables: {
          filter: {
            hasConnectedDevice: true,
            importantAlertDateRange: {
              start: moment(this.period[0]).set('second', 0).toISOString(),
              end: moment(this.period[1]).set('second', 59).toISOString(),
            },
            parentOnly: true,
          },
        },
      })

      if (resVehicleStatuses.vehicleStatusesRevamped.isSuccessful) {
        if (this.trigger === 0) {
          this.startTimeUpdates()
        }
        const vehicleStatuses = resVehicleStatuses.vehicleStatusesRevamped.vehicleStatusesRevamped
        this.vehicles = [
          ...vehicleStatuses.map((item) => {
            return { ...item.vehicle, id: item.vehicleId, licensePlate: item.licensePlate }
          }),
        ]

        vehicleStatuses.forEach((status) => {
          if (status.children && status.children.length > 0) {
            const childVehicleId = status.children[0].vehicleId
            this.childToParent[childVehicleId] = status.vehicleId
          }
        })

        const { data } = await this.$apollo.query({
          query: CTOWER_USER_PLACARDS,
          fetchPolicy: 'no-cache',
        })

        // Store initial data with timestamp for each vehicle
        vehicleStatuses.forEach((initial) => {
          initial.initialData = {
            timestamp: new Date().toISOString(),
            ...initial,
          }
        })

        this.oldDataColumn = data.ctowerUserPlacards.ctowerUserPlacards.find(
          (x) => x.vehicleId === null
        )?.asArray

        this.vehicleStatusesObj = {}
        vehicleStatuses.forEach((v) => {
          v.isPinned = false
          v.placards = this.valDef
          v.updateAlert = 0
          if (data.ctowerUserPlacards.ctowerUserPlacards.length) {
            const placard = data.ctowerUserPlacards.ctowerUserPlacards.find(
              (x) => x.vehicleId == v.vehicleId
            )
            if (placard !== undefined) {
              v.placards = placard.asArray
              v.isPinned = placard.isPinned
            } else {
              v.placards =
                this.oldDataColumn !== undefined ? [...this.oldDataColumn] : [...this.valDef]
            }
          }

          this.lastGpsLatLong[v.vehicleId] = {
            latitude: v.latitude,
            longitude: v.longitude,
          }

          let vehicleStatusesObj = {}

          vehicleStatusesObj[v.vehicleId] = { ...v }

          this.selectLocationSource(vehicleStatusesObj[v.vehicleId])

          this.vehicleStatusesObj = Object.assign(this.vehicleStatusesObj, vehicleStatusesObj)
        })

        this.currentFields = []
        this.defaultColumnOrder.forEach((d) => {
          this.currentFields.push({
            ...d,
            shown:
              this.oldDataColumn !== undefined
                ? this.oldDataColumn.includes(d.key)
                : this.valDef.includes(d.key),
          })
        })
        this.calculatedData = [...this.vehicleStatuses]

        if (this.hasLocationField) {
          this.preloadAllAddresses()
        }

        this.loading = false
      }

      this.loading = false

      await this.$nextTick()

      if (this.hasFleetTaskInstantScope) {
        this.isShowFleetTask = true
        this.fetchFleetTaskDataPeriodically()
      }
    },

    async ensureWebSocketConnected() {
      try {
        const wsToken = localStorage.getItem('wsToken')
        const payload = localStorage.getItem('payload')

        if (!wsToken) {
          return
        }

        // Use centralized WebSocket connection
        // ws/WSConnect already handles all connection logic and waiting
        if (!this.$store.state.ws.socket.isConnected) {
          const wsUrl = `${process.env.VUE_APP_WS_URL}/ws?token=${wsToken}`

          // WSConnect returns a promise that resolves when connected
          // It handles race conditions and waits for ongoing connections automatically
          await this.$store.dispatch('ws/WSConnect', wsUrl)
        }

        // Send payload if connected and not already initialized
        // Only send if App.vue hasn't already sent the initial payload
        if (
          this.$store.state.ws.socket.isConnected &&
          payload &&
          !this.$store.state.ws.isInitialized
        ) {
          this.$store.dispatch('ws/updateTags', payload)
        }
      } catch (error) {
        console.error('Failed to ensure WebSocket connection:', error)
      }
    },

    handleWebSocketMessage(message) {
      const signal = typeof message === 'string' ? JSON.parse(message) : message

      if (signal.type === 'PONG') {
        this.lastPong = moment()
        return
      }

      if (!Object.prototype.hasOwnProperty.call(signal, 'type')) return

      const userId = this.getUserId()
      const companyId = this.getCompanyId()

      if (
        this.isFilterShortcutActive &&
        !this.isCurrentShift &&
        (signal.type.startsWith('gps-') ||
          (signal.type.startsWith('alert-center-') &&
            !signal.type.startsWith('alert-center-resolve-')))
      ) {
        return
      }

      if (signal.type.startsWith('gps-')) {
        this.changeGpsByWs(signal.payload)
        this.changeTemperatureAlertByWs(signal.payload)
      } else if (signal.type === `system-notif-${userId}`) {
        const audio = new Audio(soundNotification)
        audio.play()
        this.$bvModal.hide('modal-notif')
        this.incrementUnreadAnnouncement()
        this.$mcToastNotif({
          component: 'McToastNotifDynamicBody',
          props: {
            payload: signal.payload,
            payloadHeader: this.$t('navbar.notif.newAnnouncement'),
            navbar: this,
          },
        })
      } else if (signal.type === `alert-center-resolve-${companyId}`) {
        this.resolveAlertByWs(signal.payload)
      } else if (signal.type.startsWith('alert-center-')) {
        this.changeAlertByWs(signal.payload)
        EventBus.$emit('set-alert-list', signal.payload)
      }
    },

    sortByAlertPriority(items) {
      if (!items || !Array.isArray(items)) return []

      return [...items].sort((a, b) => {
        const hasUrgentUnresolvedA = (a.controlTowerData?.urgent?.unresolvedAlerts?.length || 0) > 0
        const hasUrgentUnresolvedB = (b.controlTowerData?.urgent?.unresolvedAlerts?.length || 0) > 0
        const hasImportantUnresolvedA =
          (a.controlTowerData?.important?.unresolvedAlerts?.length || 0) > 0
        const hasImportantUnresolvedB =
          (b.controlTowerData?.important?.unresolvedAlerts?.length || 0) > 0

        const hasResolvedOnlyA =
          !hasUrgentUnresolvedA &&
          !hasImportantUnresolvedA &&
          ((a.controlTowerData?.urgent?.totalResolvedAlerts || 0) > 0 ||
            (a.controlTowerData?.important?.totalResolvedAlerts || 0) > 0)

        const hasResolvedOnlyB =
          !hasUrgentUnresolvedB &&
          !hasImportantUnresolvedB &&
          ((b.controlTowerData?.urgent?.totalResolvedAlerts || 0) > 0 ||
            (b.controlTowerData?.important?.totalResolvedAlerts || 0) > 0)

        const hasNoAlertsA = !hasUrgentUnresolvedA && !hasImportantUnresolvedA && !hasResolvedOnlyA
        const hasNoAlertsB = !hasUrgentUnresolvedB && !hasImportantUnresolvedB && !hasResolvedOnlyB

        const motionPriorityMap = { M: 1, I: 2, A: 3, P: 4, O: 5, U: 5 }
        const motionPriorityA = motionPriorityMap[a.calculatedMotionStatus] || 99
        const motionPriorityB = motionPriorityMap[b.calculatedMotionStatus] || 99

        const licensePlateA = a.licensePlate || ''
        const licensePlateB = b.licensePlate || ''

        if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1

        if (hasUrgentUnresolvedA !== hasUrgentUnresolvedB) return hasUrgentUnresolvedA ? -1 : 1

        if (hasImportantUnresolvedA !== hasImportantUnresolvedB)
          return hasImportantUnresolvedA ? -1 : 1

        if (hasResolvedOnlyA !== hasResolvedOnlyB) return hasResolvedOnlyA ? -1 : 1

        if (hasNoAlertsA !== hasNoAlertsB) return hasNoAlertsA ? 1 : -1

        if (motionPriorityA !== motionPriorityB) return motionPriorityA - motionPriorityB

        return licensePlateA.localeCompare(licensePlateB)
      })
    },

    isDataFresh(lastPositionStatus) {
      if (!lastPositionStatus) return false
      const freshWindowMinutes = Number(process.env.VUE_APP_CT_FRESH_WINDOW_MINUTES) || 5
      const freshWindowMs = freshWindowMinutes * 60 * 1000

      const lastUpdateTime = new Date(lastPositionStatus).getTime()
      const currentTime = Date.now()
      const timeDiff = currentTime - lastUpdateTime

      const isWithinWindow = timeDiff >= 0 && timeDiff < freshWindowMs
      return isWithinWindow
    },

    checkTiming(vehicleData) {
      const parentTime = new Date(vehicleData.initialData.lastPositionStatus).getTime()
      const childTime = new Date(vehicleData.initialData.children[0]?.lastPositionStatus).getTime()

      if (childTime > parentTime) {
        vehicleData.latitude = vehicleData.children[0].latitude
        vehicleData.longitude = vehicleData.children[0].longitude
        vehicleData.lastPositionStatus = vehicleData.children[0].lastPositionStatus
        vehicleData.calculatedMotionStatus = vehicleData.children[0].calculatedMotionStatus
        vehicleData.calculatedSpeed =
          vehicleData.children[0].calculatedSpeed || vehicleData.calculatedSpeed
        vehicleData.isUsingChildLocation = true
        vehicleData.deviceSource = 'cam'
        vehicleData.engineOn = vehicleData.children[0].engineOn
        vehicleData.lastReceive = vehicleData.children[0].lastReceive
      } else {
        vehicleData.isUsingChildLocation = false
        vehicleData.deviceSource = 'gps'
        vehicleData = { ...vehicleData.initialData }
      }
    },

    selectLocationSource(vehicleData) {
      if (vehicleData.deviceSource == 'cam' && vehicleData.isUsingChildLocation) {
        this.checkTiming(vehicleData)
        return
      }

      const isParentFresh = this.isDataFresh(vehicleData.initialData.lastPositionStatus)
      if (isParentFresh) {
        vehicleData.isUsingChildLocation = false
        if (vehicleData.vehicle?.sensor?.some((s) => s.sensorType?.sensorCode == 35))
          vehicleData.deviceSource = 'cam'
        else vehicleData.deviceSource = 'gps'
      } else {
        if (vehicleData.children[0]) {
          const isChildFresh = this.isDataFresh(vehicleData.children[0]?.lastPositionStatus)
          if (isChildFresh) {
            vehicleData.latitude = vehicleData.children[0].latitude
            vehicleData.longitude = vehicleData.children[0].longitude
            vehicleData.lastPositionStatus = vehicleData.children[0].lastPositionStatus
            vehicleData.calculatedMotionStatus = vehicleData.children[0].calculatedMotionStatus
            vehicleData.calculatedSpeed =
              vehicleData.children[0].calculatedSpeed || vehicleData.calculatedSpeed
            vehicleData.isUsingChildLocation = true
            vehicleData.deviceSource = 'cam'
            vehicleData.lastReceive = vehicleData.children[0].lastReceive
            vehicleData.engineOn = vehicleData.children[0].engineOn

            if (['O', 'U'].includes(vehicleData.calculatedMotionStatus)) {
              vehicleData.calculatedMotionStatus = 'M'
            }
          } else {
            // if both Parent and Child are STALE
            this.checkTiming(vehicleData)
          }
        }
      }

      // Update modal detail if it's currently showing this vehicle
      if (this.detail && this.detail.vehicleId === vehicleData.vehicleId) {
        this.detail = {
          ...this.detail,
          ...vehicleData,
        }
      }

      // Update popup data if there are active popups for this vehicle
      if (this.activePopups.length > 0) {
        const popupIndex = this.activePopups.findIndex(
          (popup) => popup.data.vehicleId === vehicleData.vehicleId
        )
        if (popupIndex >= 0) {
          this.$store.dispatch('controlTower/updatePopupData', {
            id: this.activePopups[popupIndex].id,
            data: vehicleData,
          })
        }
      }
    },

    startExpiryChecker() {
      const intervalMinutes = process.env.VUE_APP_CT_INTERVAL_EXPIRY_DRIVEN
      this.expiryCheckInterval = setInterval(() => {
        this.checkDataExpiry()
      }, intervalMinutes * 60 * 1000)
    },

    stopExpiryChecker() {
      if (this.expiryCheckInterval) {
        clearInterval(this.expiryCheckInterval)
        this.expiryCheckInterval = null
      }
    },

    checkDataExpiry() {
      if (!this.vehicleStatusesObj) return

      Object.values(this.vehicleStatusesObj).forEach((vehicle) => {
        this.selectLocationSource(vehicle)
      })
      this.trigger++
    },

    truncate3(num) {
      return Math.trunc(num * 1000) / 1000
    },

    async preloadAllAddresses() {
      if (!this.calculatedData?.length) {
        return
      }

      const itemsWithCoordinates = this.calculatedData.filter(
        (item) => item.latitude && item.longitude
      )

      const addressPromises = itemsWithCoordinates.map(async (item) => {
        const lat = this.truncate3(Number(item.latitude))
        const lon = this.truncate3(Number(item.longitude))
        const cacheKey = `${lat}_${lon}`

        if (this.addressCache[cacheKey]) {
          return
        }

        this.loadingAddresses.add(cacheKey)

        if (
          isNaN(lat) ||
          isNaN(lon) ||
          lat === null ||
          lon === 0 ||
          lat === undefined ||
          lon === undefined ||
          lat === 0 ||
          lon === 0
        ) {
          const fallback = `${item.latitude}, ${item.longitude}`
          this.$set(this.addressCache, cacheKey, fallback)
          return
        }

        try {
          const newAddress = await GetAddressData(lat, lon)
          const addressObj = newAddress.address || {}
          const kecamatan = addressObj.suburb || addressObj.village || addressObj.town || ''
          const kota = addressObj.city || addressObj.county || ''
          const simpleAddress = [kecamatan, kota].filter(Boolean).join(', ') || '-'
          this.$set(this.addressCache, cacheKey, simpleAddress)
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(`Error getting address for ${lat}, ${lon}:`, error)
          const fallback = `${lat}, ${lon}`
          this.$set(this.addressCache, cacheKey, fallback)
        } finally {
          this.loadingAddresses.delete(cacheKey)
        }
      })

      await Promise.all(addressPromises)
    },

    async fetchSingleAddress(lat, lon, cacheKey) {
      this.loadingAddresses.add(cacheKey)

      try {
        const newAddress = await GetAddressData(lat, lon)
        const addressObj = newAddress.address || {}
        const kecamatan = addressObj.suburb || addressObj.village || addressObj.town || ''
        const kota = addressObj.city || addressObj.county || ''
        const simpleAddress = [kecamatan, kota].filter(Boolean).join(', ') || '-'

        this.$set(this.addressCache, cacheKey, simpleAddress)

        this.trigger++
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`Error getting address for ${lat}, ${lon}:`, error)
        const fallback = `${lat}, ${lon}`
        this.$set(this.addressCache, cacheKey, fallback)
        this.trigger++
      } finally {
        this.loadingAddresses.delete(cacheKey)
      }
    },

    getLocationDisplay(row) {
      if (!row.latitude || !row.longitude) return '-'
      const lat = this.truncate3(Number(row.latitude))
      const lon = this.truncate3(Number(row.longitude))
      const cacheKey = `${lat}_${lon}`
      const cachedAddress = this.addressCache[cacheKey]
      if (cachedAddress) {
        return cachedAddress
      }

      if (!this.loadingAddresses.has(cacheKey)) {
        this.fetchSingleAddress(lat, lon, cacheKey)
      }

      return 'Loading address...'
    },

    onDisplayChange(type) {
      this.displayView = type
      this.trigger++
    },

    checkTemperature(ra, temp) {
      return (
        (parseFloat(ra.alertData) > ra.alertTrigger && ra.alertTrigger > temp) ||
        (parseFloat(ra.alertData) < ra.alertTrigger && ra.alertTrigger < temp)
      )
    },

    changeTemperatureAlertByWs(payload) {
      if (
        !this.vehicleStatusesObj ||
        (this.vehicleStatusesObj[payload.vehicleId] === undefined &&
          !Object.values(this.vehicleStatusesObj).some((vehicle) =>
            vehicle.children?.some((child) => child.vehicleId === payload.vehicleId)
          ))
      ) {
        return
      }

      const alerts = this.vehicleStatusesObj[payload.vehicleId]?.controlTowerData?.alerts ?? []
      const temp1 = alerts.findIndex((x) => x.alertCode === 18)
      if (
        temp1 >= 0 &&
        payload.temperature !== null &&
        this.checkTemperature(alerts[temp1], payload.temperature)
      )
        alerts.splice(temp1, 1)

      const temp2 = alerts.findIndex((x) => x.alertCode === 17)
      if (
        temp2 >= 0 &&
        payload.temperature2 !== null &&
        this.checkTemperature(alerts[temp2], payload.temperature2)
      )
        alerts.splice(temp2, 1)

      const temp3 = alerts.findIndex((x) => x.alertCode === 26)
      if (
        temp3 >= 0 &&
        payload.temperature3 !== null &&
        this.checkTemperature(alerts[temp3], payload.temperature3)
      )
        alerts.splice(temp3, 1)

      // Guard: Check if controlTowerData exists before updating status
      if (!this.vehicleStatusesObj[payload.vehicleId]?.controlTowerData) {
        return
      }

      if (alerts.length === 0 && payload.motionStatus !== 'U' && payload.motionStatus !== 'O') {
        this.vehicleStatusesObj[payload.vehicleId].controlTowerData.status = 'N'
      } else if (
        alerts.length === 0 &&
        (payload.motionStatus === 'U' || payload.motionStatus === 'O')
      ) {
        this.vehicleStatusesObj[payload.vehicleId].controlTowerData.status = 'O'
      }
    },

    changeGpsByWs(payload) {
      this.lastGpsLatLong = this.lastGpsLatLong || {}
      this.$set(this.lastGpsLatLong, payload.vehicleId, {
        latitude: payload.latitude,
        longitude: payload.longitude,
      })

      if (
        !this.vehicleStatusesObj ||
        (this.vehicleStatusesObj[payload.vehicleId] === undefined &&
          !this.childToParent[payload.vehicleId])
      ) {
        return
      }

      const parentVehicleId = this.childToParent[payload.vehicleId]
      const parentVehicle = parentVehicleId ? this.vehicleStatusesObj[parentVehicleId] : null

      if (parentVehicle) {
        // Use $set for reactivity
        const updatedChild = {
          ...parentVehicle.children[0],
          ...payload,
          calculatedSpeed: payload.calculatedSpeed,
          calculatedMotionStatus: payload.calculatedMotionStatus,
          lastPositionStatus: payload.lastPositionStatus || new Date().toISOString(),
        }
        this.$set(parentVehicle.children, 0, updatedChild)

        if (parentVehicle.initialData.children?.[0]) {
          const updatedInitialChild = {
            ...parentVehicle.initialData.children[0],
            ...payload,
            calculatedSpeed: payload.calculatedSpeed,
            calculatedMotionStatus: payload.calculatedMotionStatus,
            lastPositionStatus: payload.lastPositionStatus || new Date().toISOString(),
          }
          this.$set(parentVehicle.initialData.children, 0, updatedInitialChild)
        }

        this.selectLocationSource(parentVehicle)
      } else {
        const vehicle = this.vehicleStatusesObj[payload.vehicleId]

        if (!vehicle) return

        if (vehicle.deviceSource === 'cam' && vehicle.children?.[0]) {
          const parentTimestamp = new Date(payload.lastPositionStatus || new Date()).getTime()
          const childTimestamp = new Date(vehicle.children[0].lastPositionStatus).getTime()

          if (parentTimestamp <= childTimestamp) {
            // Use $set for nested object
            this.$set(this.vehicleStatusesObj[payload.vehicleId], 'initialData', {
              ...this.vehicleStatusesObj[payload.vehicleId].initialData,
              ...payload,
              lastPositionStatus: payload.lastPositionStatus || new Date().toISOString(),
            })
            return
          }
        }

        // Update vehicle with $set for reactivity
        const updatedVehicle = {
          ...vehicle,
          ...payload,
          deviceSource: 'gps',
          isUsingChildLocation: false,
          initialData: {
            ...vehicle.initialData,
            ...payload,
            lastPositionStatus: payload.lastPositionStatus || new Date().toISOString(),
          },
        }
        this.$set(this.vehicleStatusesObj, payload.vehicleId, updatedVehicle)
      }

      // Guard: Check if vehicle exists and has placards before processing (removed duplicate)
      if (!this.vehicleStatusesObj[payload.vehicleId]?.placards) {
        return
      }

      const placards = [
        ...this.vehicleStatusesObj[payload.vehicleId].placards.filter((x) => x !== null),
      ]

      placards.forEach((p) => {
        if (this.listKeys[p].key === 'latitude' || this.listKeys[p].key === 'longitude') return

        let finalValue
        if (this.listKeys[p].key === 'temperature') {
          finalValue = [
            payload.temperature ?? null,
            payload.temperature2 ?? null,
            payload.temperature3 ?? null,
            payload.temperature4 ?? null,
          ]
        } else {
          finalValue = payload[this.listKeys[p].key]
        }

        // Use $set for reactivity
        this.$set(this.vehicleStatusesObj[payload.vehicleId], this.listKeys[p].key, finalValue)
      })

      if (this.detail?.vehicleId === payload.vehicleId) {
        this.detail = {
          ...this.detail,
          ...this.vehicleStatusesObj[payload.vehicleId],
        }

        this.$store.dispatch('controlTower/updatePopupData', {
          vehicleId: payload.vehicleId,
          data: this.detail,
        })
      }

      // Use map to create new array for reactivity
      this.calculatedData = this.calculatedData.map((item) => {
        if (item.vehicleId === payload.vehicleId) {
          return {
            ...item,
            ...this.vehicleStatusesObj[payload.vehicleId],
            licensePlate: item.licensePlate,
            lastReceive: new Date().toISOString(),
            timePassed: 0,
          }
        }
        return item
      })

      this.trigger++
    },

    applyBufferedLatLongUpdates() {
      if (!this.lastGpsLatLong) return
      Object.entries(this.lastGpsLatLong).forEach(([vehicleId, coords]) => {
        if (this.vehicleStatusesObj[vehicleId]) {
          this.vehicleStatusesObj[vehicleId].latitude = coords.latitude
          this.vehicleStatusesObj[vehicleId].longitude = coords.longitude
        }
      })
      this.trigger++
      this.lastGpsLatLong = {}

      if (this.hasLocationField) {
        this.preloadAllAddresses()
      }
    },

    isAlertUrgent(formattedAlert) {
      // Guard: Check if alert rules are loaded as arrays
      if (!Array.isArray(this.urgentAlertRules) || !formattedAlert?.alertRuleIds) {
        return false
      }

      const hasMatchingRule = formattedAlert.alertRuleIds.some((ruleId) =>
        this.urgentAlertRules?.includes(ruleId)
      )

      return hasMatchingRule
    },

    isAlertImportant(formattedAlert) {
      if (this.isAlertUrgent(formattedAlert)) {
        return false
      }

      // Guard: Check if alert rules are loaded as arrays
      if (!Array.isArray(this.importantAlertRules) || !formattedAlert?.alertRuleIds) {
        return false
      }

      const hasMatchingImportantRule = formattedAlert.alertRuleIds.some((ruleId) =>
        this.importantAlertRules?.includes(ruleId)
      )

      return hasMatchingImportantRule
    },

    addAlertToControlTowerData(oldControlTowerData, alertType, newAlert) {
      const oldTypeData = oldControlTowerData[alertType] || {}
      const oldAlerts = oldTypeData.alerts || []
      const oldUnresolvedAlerts = oldTypeData.unresolvedAlerts || []
      const newUnresolvedLength = oldUnresolvedAlerts.length + 1

      return {
        ...oldControlTowerData,
        [alertType]: {
          ...oldTypeData,
          alerts: [newAlert, ...oldAlerts],
          unresolvedAlerts: [newAlert, ...oldUnresolvedAlerts],
          totalUnresolvedAlerts: (oldTypeData.totalUnresolvedAlerts || 0) + 1,
          totalOtherUnresolvedAlerts:
            newUnresolvedLength >= 5
              ? (oldTypeData.totalOtherUnresolvedAlerts || 0) + 1
              : oldTypeData.totalOtherUnresolvedAlerts || 0,
        },
        totalUnresolvedAlerts: (oldControlTowerData.totalUnresolvedAlerts || 0) + 1,
      }
    },

    resolveAlertInType(oldTypeData, rawAlertId, resolveData) {
      const alertIndex = oldTypeData.alerts?.findIndex((x) => x.rawAlertId === rawAlertId)
      if (alertIndex < 0) return null

      const oldAlerts = oldTypeData.alerts || []
      const oldUnresolvedAlerts = oldTypeData.unresolvedAlerts || []

      const newAlerts = [...oldAlerts]
      newAlerts[alertIndex] = {
        ...oldAlerts[alertIndex],
        resolvedChangedBy: resolveData.userId,
        resolvedChangedAt: resolveData.endDate,
        resolver: resolveData.userName,
        isResolved: resolveData.isResolved,
      }

      let newUnresolvedAlerts = [...oldUnresolvedAlerts]
      let newTotalUnresolvedAlerts = oldTypeData.totalUnresolvedAlerts || 0
      let newTotalResolvedAlerts = oldTypeData.totalResolvedAlerts || 0

      if (resolveData.isResolved) {
        newUnresolvedAlerts = oldUnresolvedAlerts.filter((x) => x.rawAlertId !== rawAlertId)
        newTotalUnresolvedAlerts = Math.max(0, newTotalUnresolvedAlerts - 1)
        newTotalResolvedAlerts = newTotalResolvedAlerts + 1
      } else {
        const alreadyUnresolved = oldUnresolvedAlerts.some((x) => x.rawAlertId === rawAlertId)
        if (!alreadyUnresolved) {
          newUnresolvedAlerts = [newAlerts[alertIndex], ...oldUnresolvedAlerts]
          newTotalUnresolvedAlerts = newTotalUnresolvedAlerts + 1
        }
        newTotalResolvedAlerts = Math.max(0, newTotalResolvedAlerts - 1)
      }

      return {
        ...oldTypeData,
        alerts: newAlerts,
        unresolvedAlerts: newUnresolvedAlerts,
        totalUnresolvedAlerts: newTotalUnresolvedAlerts,
        totalResolvedAlerts: newTotalResolvedAlerts,
      }
    },

    resolveAllAlertsInType(oldTypeData, value, resolveData) {
      const updatedAlerts = oldTypeData.alerts.map((alert) => ({
        ...alert,
        resolvedChangedBy: resolveData.userId,
        resolvedChangedAt: resolveData.endDate,
        resolver: resolveData.userName,
        isResolved: resolveData.isResolved,
      }))

      let newUnresolvedAlerts
      let newTotalUnresolvedAlerts
      let newTotalResolvedAlerts

      if (resolveData.isResolved) {
        newUnresolvedAlerts = []
        newTotalResolvedAlerts = (oldTypeData.totalResolvedAlerts || 0) + value
        newTotalUnresolvedAlerts = Math.max(0, (oldTypeData.totalUnresolvedAlerts || 0) - value)
      } else {
        newUnresolvedAlerts = [...updatedAlerts]
        newTotalResolvedAlerts = Math.max(0, (oldTypeData.totalResolvedAlerts || 0) - value)
        newTotalUnresolvedAlerts = (oldTypeData.totalUnresolvedAlerts || 0) + value
      }

      return {
        ...oldTypeData,
        alerts: updatedAlerts,
        unresolvedAlerts: newUnresolvedAlerts,
        totalResolvedAlerts: newTotalResolvedAlerts,
        totalUnresolvedAlerts: newTotalUnresolvedAlerts,
      }
    },

    updateVehicleWithResolvedAlerts(vehicleId, newControlTowerData, oldVehicle) {
      const urgentUnresolved = newControlTowerData.urgent?.totalUnresolvedAlerts || 0
      const importantUnresolved = newControlTowerData.important?.totalUnresolvedAlerts || 0
      newControlTowerData.totalUnresolvedAlerts = urgentUnresolved + importantUnresolved

      const urgentResolved = newControlTowerData.urgent?.totalResolvedAlerts || 0
      const importantResolved = newControlTowerData.important?.totalResolvedAlerts || 0
      newControlTowerData.totalResolvedAlerts = urgentResolved + importantResolved

      const newVehicle = {
        ...oldVehicle,
        controlTowerData: newControlTowerData,
        updateAlert: (oldVehicle.updateAlert || 0) + 1,
      }

      this.$set(this.vehicleStatusesObj, vehicleId, newVehicle)

      if (this.activePopups.length > 0) {
        const popup = this.activePopups.find((p) => p.data.vehicleId === vehicleId)
        if (popup) {
          this.$store.dispatch('controlTower/updatePopupData', {
            id: popup.id,
            data: newVehicle,
          })
        }
      }
    },

    changeAlertByWs(payload) {
      if (!this.isAlertUrgent(payload) && !this.isAlertImportant(payload)) {
        return
      }

      if (
        !this.vehicleStatusesObj ||
        this.vehicleStatusesObj[payload.vehicleId] === undefined ||
        !this.vehicleStatusesObj[payload.vehicleId].controlTowerData
      ) {
        return
      }

      const alertDate = moment(payload.createdOn).format('YYYY-MM-DD')
      const periodStart = this.period[0]
      const periodEnd = this.period[1]

      const isWithinPeriod = moment(alertDate).isBetween(periodStart, periodEnd, 'day', '[]')

      if (!isWithinPeriod) {
        return
      }

      const oldVehicle = this.vehicleStatusesObj[payload.vehicleId]
      const oldControlTowerData = oldVehicle.controlTowerData

      const id = payload.alertRuleIds?.[0]
      const { autoPopupAlertRules, urgentAlertRules, importantAlertRules } =
        this.$store.state.notification?.alertRuleObjs || {}

      const alertRule = id
        ? autoPopupAlertRules?.[id] ?? urgentAlertRules?.[id] ?? importantAlertRules?.[id] ?? null
        : null

      const newAlert = {
        ...payload,
        alert: {
          descriptionEng: payload.alertDescription.eng,
          descriptionIdn: payload.alertDescription.idn,
          iconUrl: payload.iconUrl,
        },
        alertCode: payload.alertCode,
        alertData: payload.alertData,
        alertTrigger: parseFloat(payload.alertTrigger),
        createdOn: payload.createdOn,
        vehicleId: payload.vehicleId,
        alertFlag: payload.alertFlag,
        rawAlertId: payload.rawAlertId,
        isUrgent: this.isAlertUrgent(payload),
        isImportant: this.isAlertImportant(payload),
        isResolved: false,
        isRead: false,
        alertRule: alertRule || null,
      }

      // Use helper function to add alert - simplified!
      const alertType = newAlert.isUrgent ? 'urgent' : 'important'
      const newControlTowerData = this.addAlertToControlTowerData(
        oldControlTowerData,
        alertType,
        newAlert
      )

      // Create NEW vehicle object
      const newVehicle = {
        ...oldVehicle,
        controlTowerData: newControlTowerData,
      }

      // Update vehicleStatusesObj with $set for reactivity
      this.$set(this.vehicleStatusesObj, payload.vehicleId, newVehicle)

      // Update calculatedData from vehicleStatuses (which handles sorting)
      this.calculatedData = [...this.vehicleStatuses]

      // Manually update active popups if any
      if (this.activePopups.length > 0) {
        const popup = this.activePopups.find((p) => p.data.vehicleId === payload.vehicleId)
        if (popup) {
          this.$store.dispatch('controlTower/updatePopupData', {
            id: popup.id,
            data: newVehicle,
          })
        }
      }

      this.trigger++
    },

    resolveAlertByWs(payload) {
      const decoded = msgpack.decode(payload.data)
      let mode
      if (decoded.m === 'a') {
        mode = 'All'
      } else if (decoded.m === 's') {
        mode = 'Selection'
      } else {
        mode = 'Exclude'
      }
      const data = {
        mode: mode,
        startDate: new Date(decoded.st),
        endDate: new Date(decoded.et),
        isResolved: decoded.r === 1,
        userId: decoded.u,
        userName: decoded.un,
        vehicles: new Map(decoded.v),
        rawAlertId: decoded.id?.map((id) => Number(id)),
      }

      const isDateInRange = this.isDateWithinPeriod(data.startDate, data.endDate)
      if (!isDateInRange) {
        return
      }

      if (data.mode == 'Selection') {
        data.rawAlertId.forEach((rawAlertId) => {
          data.vehicles.forEach((value, vehicleId) => {
            if (!this.vehicleStatusesObj[vehicleId]) return

            const oldVehicle = this.vehicleStatusesObj[vehicleId]
            const oldControlTowerData = oldVehicle.controlTowerData
            let newControlTowerData = { ...oldControlTowerData }
            let alertUpdated = false

            // Use helper function for urgent alerts
            if (oldControlTowerData?.urgent?.alerts) {
              const newUrgent = this.resolveAlertInType(
                oldControlTowerData.urgent,
                rawAlertId,
                data
              )
              if (newUrgent) {
                newControlTowerData.urgent = newUrgent
                alertUpdated = true
              }
            }

            // Use helper function for important alerts
            if (oldControlTowerData?.important?.alerts) {
              const newImportant = this.resolveAlertInType(
                oldControlTowerData.important,
                rawAlertId,
                data
              )
              if (newImportant) {
                newControlTowerData.important = newImportant
                alertUpdated = true
              }
            }

            // Only update if alert was found and modified
            if (alertUpdated) {
              this.updateVehicleWithResolvedAlerts(vehicleId, newControlTowerData, oldVehicle)
            }
          })
        })

        this.calculatedData = [...this.vehicleStatuses]
      }

      if (data.mode === 'All') {
        data.vehicles.forEach((value, vehicleId) => {
          if (!this.vehicleStatusesObj[vehicleId]) return

          const oldVehicle = this.vehicleStatusesObj[vehicleId]
          const oldControlTowerData = oldVehicle.controlTowerData
          let newControlTowerData = { ...oldControlTowerData }

          // Use helper function for urgent alerts
          if (oldControlTowerData?.urgent?.alerts) {
            newControlTowerData.urgent = this.resolveAllAlertsInType(
              oldControlTowerData.urgent,
              value,
              data
            )
          }

          // Use helper function for important alerts
          if (oldControlTowerData?.important?.alerts) {
            newControlTowerData.important = this.resolveAllAlertsInType(
              oldControlTowerData.important,
              value,
              data
            )
          }

          this.updateVehicleWithResolvedAlerts(vehicleId, newControlTowerData, oldVehicle)
        })

        this.calculatedData = [...this.vehicleStatuses]
      }

      this.trigger++
    },

    isDateWithinPeriod(startDate, endDate) {
      const periodStart = moment(this.period[0]).startOf('day')
      const periodEnd = moment(this.period[1]).endOf('day')

      const dataStart = moment(startDate)
      const dataEnd = moment(endDate)

      const startInRange = dataStart.isBetween(periodStart, periodEnd, 'day', '[]')
      const endInRange = dataEnd.isBetween(periodStart, periodEnd, 'day', '[]')

      const rangeOverlaps =
        dataStart.isSameOrBefore(periodEnd) && dataEnd.isSameOrAfter(periodStart)

      return startInRange || endInRange || rangeOverlaps
    },

    async showDetail(row) {
      this.track('Click Vehicle Card on Control Tower V2')
      this.detail = { ...row }

      if (this.hasFleetTaskInstantScope) {
        this.getFleetTaskList([row.vehicleId])
      }

      let callState = {}

      if (!this.callState[row.vehicleId]) {
        const isHaveChildren = row.children?.length > 0
        let deviceType = this.getDeviceType(row.vehicle.device.deviceCode)
        let imeiVoip = row.vehicle.imei
        let vehicleIdVoip = row.vehicleId
        if (isHaveChildren) {
          deviceType = this.getDeviceType(row.children[0].vehicle.device?.deviceCode)
          imeiVoip = row.children[0].vehicle.imei
          vehicleIdVoip = row.children[0].vehicleId
        }
        const isHaveVoip = deviceType === 'bsj' || deviceType === 'vss-howen'
        callState = {
          isHaveVoip,
          deviceType,
          imeiVoip,
          vehicleIdVoip,
        }
      } else {
        callState = this.callState[row.vehicleId]
      }

      this.callState[row.vehicleId] = callState

      const popupData = {
        ...row,
      }

      await this.$nextTick()
      this.$store.dispatch('controlTower/addPopup', { ctData: popupData, callState })
    },

    setPin(row) {
      this.$apollo
        .mutate({
          mutation: UPDATE_CTOWER_USER_PLACARD,
          variables: {
            input: {
              vehicleId: row.vehicleId,
              oldData: {
                isPinned: row.isPinned,
                column: this.oldDataColumn,
              },
              newData: {
                isPinned: !row.isPinned,
                column: row.placards,
              },
            },
          },
        })
        .then(async ({ data }) => {
          if (data.updateCtowerUserPlacard.isSuccessful) {
            const temp = { ...this.vehicleStatusesObj }
            this.vehicleStatusesObj = {}
            temp[row.vehicleId].isPinned = !temp[row.vehicleId].isPinned
            await this.$nextTick()
            this.vehicleStatusesObj = { ...temp }
            setTimeout(() => {
              this.trigger++
            }, 100)
          }
        })
    },

    async onFiltered(value) {
      this.loading = true
      const temp = { ...this.vehicleStatusesObj }
      this.filter.vehicleId = this.findFilterValue(value, 'vehicleId') || []
      this.filter.motionStatus = this.findFilterValue(value, 'motionStatus') || [
        'M',
        'I',
        'A',
        'P',
        'O',
      ]
      this.filter.assignmentType = this.findFilterValue(value, 'assignmentType') || 'all'
      await this.$nextTick()
      this.vehicleStatusesObj = { ...temp }
      setTimeout(() => {
        this.trigger++
        this.loading = false
      }, 100)
    },

    globalSettingField(column) {
      this.loading = true
      let hasLocation = false
      const arrColumn = []
      column.forEach((item) => {
        if (item.key === 'fleetTaskNumber') {
          this.isShowFleetTask = item.shown

          return
        }

        if (item.key === 'location') {
          hasLocation = item.shown
        }

        if (!item.shown) return

        arrColumn.push(item.key)
      })

      if (this.isShowFleetTask && this.intervalFleetTask === null) {
        this.fetchFleetTaskDataPeriodically()
      } else if (!this.isShowTask && this.intervalFleetTask !== null) {
        clearTimeout(this.pageViewTimer)

        this.clearFleetTaskTimeInterval()
        window.removeEventListener('focus', this.fetchFleetTaskOnTabFocus)
        window.removeEventListener('blur', this.clearFleetTaskTimeInterval)
      }

      if (hasLocation) {
        this.preloadAllAddresses()
      }

      this.$apollo
        .mutate({
          mutation: UPDATE_CTOWER_USER_PLACARD,
          variables: {
            input: {
              oldData: {
                column: this.oldDataColumn,
              },
              newData: {
                column: arrColumn,
              },
              vehicleId: null,
            },
          },
        })
        .then(async ({ data }) => {
          if (data.updateCtowerUserPlacard.isSuccessful) {
            const temp = { ...this.vehicleStatusesObj }
            Object.keys(temp).forEach((key) => {
              temp[key].placards = arrColumn
            })
            await this.$nextTick()
            this.vehicleStatusesObj = { ...temp }
            setTimeout(() => {
              this.trigger++
              this.loading = false
            }, 100)
          }
        })
    },

    toggleExpand() {
      this.isExpanded = !this.isExpanded
      this.track(
        this.isExpanded
          ? 'Maximize Vehicles on Control Tower V2'
          : 'Minimize Vehicles on Control Tower V2'
      )
    },

    handleShowUnresolved(value) {
      this.showUnresolved = value
      this.trigger++
    },

    async showUnresolvedAlerts() {
      this.activeUnresolvedPeriod = this.period
      await this.$nextTick()
      this.$bvModal.show('modal-unresolved')
    },

    showPreviousShiftUnresolvedAlerts() {
      this.activeUnresolvedPeriod = this.previousShiftPeriod
      this.$bvModal.show('modal-unresolved')
    },

    handleResolvedAlerts() {
      if (this.activeUnresolvedPeriod === this.previousShiftPeriod) {
        this.fetchPreviousShiftStats(this.previousShiftPeriod)
      }
    },

    showResolvedAlerts() {
      this.$bvModal.show('modal-resolved')
    },

    openShiftScheduleModal() {
      this.$bvModal.show('modal-shift-schedule')
    },

    openNotificationSetting() {
      this.track('Open Pengaturan Notifikasi from Control Tower V2')
      this.$router.push({ name: 'Setting Notification List' })
    },

    async getFleetTaskList(vehicleIds = []) {
      const filters = ['status.name eq STARTED']

      if (vehicleIds?.length > 0) {
        const vehicleFilter = `vehicle.id in (${vehicleIds.join(' ')})`
        filters.push(vehicleFilter)
      }

      const res = await FleetTaskInstant.get(filters)({
        limit: Number.MAX_SAFE_INTEGER,
        page: 1,
      })

      if (res.errors || res.metadata?.count === 0) return

      const dataMap = new Map()
      res.data.paginated_result.forEach((item) => {
        dataMap.set(item.vehicle.id, item)

        if (this.vehicleStatusesObj[item.vehicle.id] === undefined) return

        this.$set(this.vehicleStatusesObj[item.vehicle.id]?.controlTowerData, 'fleetTaskInstant', {
          id: item.id,
          number: item.number,
          timeline_route: item.timeline_route.map((item) => {
            return {
              id: item.id,
              visit_status: item.visit_status,
              point_type: item.point_type,
              address: {
                name: item.address.name,
              },
            }
          }),
        })
      })

      this.fleetTaskMap.forEach((value, key) => {
        if (dataMap.get(key)) return

        this.$delete(this.vehicleStatusesObj[key]?.controlTowerData, 'fleetTaskInstant')
      })

      this.fleetTaskMap = dataMap

      if (this.popupLists.length > 0) {
        this.$store.dispatch('controlTower/setActivePopupsToLocalStorage')
      }
    },

    fetchFleetTaskDataPeriodically() {
      clearInterval(this.intervalFleetTask)

      this.getFleetTaskList()
      this.intervalFleetTask = setInterval(async () => {
        await this.getFleetTaskList()
      }, this.timeIntervalFO)
    },

    clearFleetTaskTimeInterval() {
      clearInterval(this.intervalFleetTask)
      this.intervalFleetTask = null
    },

    fetchFleetTaskOnTabFocus() {
      this.fetchFleetTaskDataPeriodically()
    },

    setIntervalGetFleetTask() {
      this.fetchFleetTaskDataPeriodically()

      window.addEventListener('focus', this.fetchFleetTaskOnTabFocus)
      window.addEventListener('blur', this.clearFleetTaskTimeInterval)
    },

    pickDate(date) {
      this.isFilterShortcutActive = false
      this.previousShiftUnresolvedAlerts = 0
      const maxDate = 7
      this.pastDate = new Date(date.getTime() - 24 * maxDate * 3600 * 1000)
      this.currentPastDate = new Date(date.getTime() - 24 * maxDate * 3600 * 1000)
      const today = new Date()
      if (date.getTime() + 24 * 0 * 3600 * 1000 === today.setHours(0, 0, 0, 0)) {
        this.currentFutureEnd = new Date(date.getTime() + 24 * 1 * 3600 * 1000)
      } else {
        this.currentFutureEnd = new Date(date.getTime() + 24 * maxDate * 3600 * 1000)
      }
    },

    closePicker() {
      this.currentPastDate = moment().subtract(6, 'months')
      this.currentFutureEnd = moment().add(6, 'months')
    },

    disabledRange: function (date) {
      let limit = new Date()

      return (
        date <= this.pastDate ||
        date >= this.futureEnd ||
        date <= this.currentPastDate ||
        date >= this.currentFutureEnd ||
        date > limit
      )
    },

    async changeDate() {
      this.addressCache = {}
      await this.getData()
    },

    updateCurrentTime() {
      this.currentTime = moment()
    },

    startTimeUpdates() {
      this.timeUpdateInterval = setInterval(() => {
        this.updateCurrentTime()
      }, 1000)
    },

    async getDataTrackvision(vehicleIdCam) {
      try {
        const response = await this.$apollo.query({
          query: GET_VEHICLE_TV_DATA,
          fetchPolicy: 'no-cache',
          variables: {
            filter: {
              id: vehicleIdCam,
            },
          },
        })
        const dataTv = response.data?.vehicle?.vehicle ?? null
        return dataTv
      } catch (error) {
        console.error(error)
        return null
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import './style.scss';
</style>
